/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import { BETabsProps } from './BETabsProps';
import BETab from './components/Tab';
import BETabPanel from './components/TabPanel';
import { StyledTabList, StyledTabs } from './StyledTabs';

export const TabsContext = createContext<
  | {
      currentTab: string;
      setCurrentTab: (tabId: string) => void;
    }
  | undefined
>(undefined);

const BETabs: React.ForwardRefExoticComponent<
  React.PropsWithoutRef<BETabsProps> & React.RefAttributes<HTMLDivElement>
> = forwardRef(({ activeTab, children, disabled, tablist, ...rest }, ref) => {
  const tablistEl = useRef<HTMLDivElement>(null);
  const [focusedTab, setFocusedTab] = useState<string>();
  const [currentTab, setCurrentTab] = useState(activeTab);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!tablistEl?.current) {
      return;
    }

    const key = e.key;
    const focusableTabs = [
      ...tablistEl.current.querySelectorAll(
        `[role="tab"]:not([aria-disabled])`
      ),
    ];
    const focusedTab = e.currentTarget as HTMLAnchorElement | HTMLButtonElement;
    const focusedIndex = focusableTabs.findIndex(
      (tab) =>
        tab.getAttribute('aria-controls') ===
        focusedTab.getAttribute('aria-controls')
    );

    const prevFocusTab = focusableTabs[focusedIndex - 1] as
      | HTMLAnchorElement
      | HTMLButtonElement;
    const nextFocusTab = focusableTabs[focusedIndex + 1] as
      | HTMLAnchorElement
      | HTMLButtonElement;
    const firstFocusTab = focusableTabs[0] as
      | HTMLAnchorElement
      | HTMLButtonElement;

    if (
      prevFocusTab &&
      typeof prevFocusTab.focus === 'function' &&
      (key === 'ArrowLeft' || key === 'ArrowUp')
    ) {
      e.preventDefault();
      const id = prevFocusTab.getAttribute('id');
      if (id) {
        setFocusedTab(id);
      }
    }

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      if (nextFocusTab && typeof nextFocusTab.focus === 'function') {
        e.preventDefault();
        const id = nextFocusTab.getAttribute('id');
        if (id) {
          setFocusedTab(id);
        }
      }
    }
  };

  useEffect(() => {
    // On load
    if (!focusedTab) {
      const tabs = [
        ...(tablistEl.current?.querySelectorAll(`[role="tab"]`) || []),
      ];

      tabs.forEach((tab) => {
        tab.setAttribute(
          'tabindex',
          tab.getAttribute('id') !== currentTab ? '-1' : '0'
        );
      });

      return;
    }

    const focusedTabEl = document.getElementById(focusedTab) as
      | HTMLAnchorElement
      | HTMLButtonElement;

    const tabs = [
      ...(tablistEl.current?.querySelectorAll(`[role="tab"]`) || []),
    ];

    if (focusedTabEl && typeof focusedTabEl.focus === 'function') {
      focusedTabEl.setAttribute('tabindex', '0');
      focusedTabEl.focus();
    }

    tabs.forEach((tab) => {
      if (tab.getAttribute('id') !== focusedTab) {
        tab.setAttribute('tabindex', '-1');
      }
    });
  }, [focusedTab, currentTab]);

  const contextValue = {
    currentTab,
    setCurrentTab: (tabId: string) => setCurrentTab(tabId),
  };

  return (
    <TabsContext.Provider value={contextValue}>
      <StyledTabs {...rest} ref={ref}>
        <StyledTabList ref={tablistEl} role="tablist">
          {Children.map(tablist, (child) => {
            return cloneElement(child, {
              onKeyDown: handleKeyDown,
              role: 'tab',
            });
          })}
        </StyledTabList>
        {Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            const panelActive = child.props.tabId === currentTab;

            return cloneElement(child as any, {
              hidden: !panelActive,
            });
          }
        })}
      </StyledTabs>
    </TabsContext.Provider>
  );
});

export const useTabsContext = () => {
  const context = useContext(TabsContext);

  if (!context) {
    throw new Error('useTabsContext must be used within a TabsProvider');
  }

  return context;
};

export { BETabs, BETab, BETabPanel };
